import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarDays, CheckCircle, AlertCircle, Loader2, Info, ExternalLink } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { calendarService, connectCalendarProvider } from "@/services/calendarService";

interface CalendarIntegrationStepProps {
  onSkip?: () => void;
}

const CalendarIntegrationStep = ({ onSkip }: CalendarIntegrationStepProps) => {
  const [connectionState, setConnectionState] = useState<'idle' | 'connecting' | 'connected' | 'error'>('idle');
  const [showPopupHelp, setShowPopupHelp] = useState(false);

  const { toast } = useToast();
  const { user } = useAuth();

  // Check if calendar is already connected when component loads
  useEffect(() => {
    const checkExistingConnection = async () => {
      if (!user?.id) return;
      
      try {
        const result = await calendarService.getIntegrations();
        // apiService.get returns data directly (not { success, data })
        const integrations = Array.isArray(result) ? result : (result?.integrations || []);
        
        if (integrations.some((int: any) => int.provider === 'google')) {
          setConnectionState('connected');
        }
      } catch (error) {
        // No existing connection, stay in idle state
        console.log('No existing calendar connection found');
      }
    };

    checkExistingConnection();
  }, [user?.id]);

  const handleConnect = async () => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to connect your calendar.",
        variant: "destructive",
      });
      return;
    }

    setConnectionState('connecting');

    try {
      const success = await connectCalendarProvider('google', '', user.id);
      if (success) {
        setConnectionState('connected');
        toast({
          title: "Calendar connected",
          description: "Your Google Calendar has been successfully connected!",
        });
      } else {
        setConnectionState('error');
        toast({
          title: "Connection failed",
          description: "Failed to connect calendar. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("Error connecting Google calendar:", error);
      setConnectionState('error');
      
      toast({
        title: "Connection failed",
        description: error.message || "Failed to initiate calendar connection.",
        variant: "destructive",
      });
    }
  };

  const getButtonState = () => {
    switch (connectionState) {
      case 'connecting':
        return { text: 'Connecting...', disabled: true, icon: <Loader2 className="h-4 w-4 animate-spin" /> };
      case 'connected':
        return { text: 'Connected', disabled: true, icon: <CheckCircle className="h-4 w-4 text-green-600" /> };
      case 'error':
        return { text: 'Retry', disabled: false, icon: <AlertCircle className="h-4 w-4 text-red-600" /> };
      default:
        return { text: 'Connect', disabled: false, icon: <CalendarDays className="h-4 w-4" /> };
    }
  };

  const buttonState = getButtonState();

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="mb-3">
          <CalendarDays className="h-12 w-12 text-primary mx-auto mb-3" />
        </div>
        <p className="text-sm text-muted-foreground">
          Connect your Google Calendar to sync events with your daily check-ins and get personalized scheduling insights
        </p>
      </div>

      <div className="grid gap-4">
        <Card className={`${
          connectionState === 'connected' 
            ? 'bg-gradient-to-r from-green-50 to-green-100 border-green-200' 
            : 'bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200'
        } hover:shadow-md transition-all duration-200`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="text-2xl">📅</div>
                <div>
                  <h3 className="font-semibold text-sm">Google Calendar</h3>
                  <p className="text-xs text-muted-foreground">
                    {connectionState === 'connected' 
                      ? 'Successfully connected and synced' 
                      : 'Sync with Google Calendar'}
                  </p>
                </div>
              </div>
              
              <Button
                onClick={handleConnect}
                disabled={buttonState.disabled}
                variant={connectionState === 'connected' ? 'default' : 'outline'}
                size="sm"
                className={`flex items-center gap-2 ${
                  connectionState === 'connected' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : ''
                }`}
              >
                {buttonState.icon}
                {buttonState.text}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Popup Help Section - Show when connection fails or user needs guidance */}
      {(showPopupHelp || connectionState === 'error') && (
        <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <CardContent className="p-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-blue-900 dark:text-blue-100">
                    Having trouble connecting?
                  </h4>
                  <div className="text-xs text-blue-700 dark:text-blue-300 space-y-2">
                    <p>Follow these steps to connect your calendar successfully:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Make sure pop-ups are allowed for this website</li>
                      <li>Click "Connect" and look for a pop-up window</li>
                      <li>If no pop-up appears, check your browser's pop-up blocker</li>
                      <li>In the pop-up, sign in to your Google account</li>
                      <li>Grant permission to access your calendar</li>
                    </ol>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-blue-200 dark:border-blue-700 pt-3">
                <div className="flex items-start gap-2 text-xs text-blue-600 dark:text-blue-400">
                  <ExternalLink className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Browser tip:</strong> Look for a popup blocked notification in your address bar or browser toolbar
                  </span>
                </div>
              </div>
              
              <Button 
                onClick={() => setShowPopupHelp(false)}
                variant="ghost" 
                size="sm"
                className="w-full text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800"
              >
                Got it, hide this help
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="text-center space-y-3">
        {connectionState === 'connected' ? (
          <div className="space-y-3">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
              <div className="flex items-center justify-center gap-2 text-green-700 dark:text-green-400">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Calendar Connected Successfully!</span>
              </div>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                Your Google Calendar is now synced with Step
              </p>
            </div>
            <Button 
              onClick={onSkip}
              className="w-full"
              size="sm"
            >
              Continue to Next Step
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2 justify-center">
              <Button 
                onClick={onSkip}
                variant="ghost" 
                className="text-sm"
              >
                Skip for now
              </Button>
              
              {!showPopupHelp && (
                <Button 
                  onClick={() => setShowPopupHelp(true)}
                  variant="ghost" 
                  size="sm"
                  className="text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                >
                  <Info className="h-3 w-3 mr-1" />
                  Need help?
                </Button>
              )}
            </div>
            
            <p className="text-xs text-muted-foreground">
              You can always connect your calendar later in your profile settings
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarIntegrationStep;